import React, {useCallback, useEffect, useState} from "react";
import $ from 'jquery';
import {routes} from "../../../core/models/static/routes/app-routes";
import Button from "../../components/base/button/button";
import useIsMounted from "../../hooks/use-is-mounted";
import {ReactComponent as StemMask} from "../../../assets/images/versions/stem-mask.svg";
import {ReactComponent as StemIcon} from "../../../assets/images/versions/stem-icon.svg";
import {ReactComponent as ScrollToTopIcon} from "../../../assets/images/versions/scroll-to-top-icon.svg";
import DateUtils from "../../../core/services/date-utils";
import {IconButton, Typography} from "@mui/material";
import {OndemandVideoOutlined} from "@mui/icons-material";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import useAccessLevels from "../../hooks/use-access-levels";
import Api from "../../../core/services/api/api";

/**
 * @typedef {Object} NormalizedVersionNote
 * @property {string} text
 * @property {string | null} videoUrl
 */

/**
 * Normalizes a version-history note entry. Supports legacy plain strings and
 * objects with optional {@link videoUrl}.
 *
 * @param {string | { text?: string, videoUrl?: string }} note
 * @return {NormalizedVersionNote}
 */
function normalizeVersionNote(note) {
    if (typeof note === 'string') {
        return {text: note, videoUrl: null};
    }

    return {
        text: note?.text ?? '',
        videoUrl: note?.videoUrl ?? null,
    };
}

/**
 * Reads the whole-release overview video URL stored beside {@link notes} on the version entry.
 * Falls back to a legacy {@link videoUrl} nested inside {@link notes} when present.
 *
 * @param {unknown} versionEntry
 * @return {string | null}
 */
function getVersionVideoUrl(versionEntry) {
    if (!versionEntry || typeof versionEntry !== 'object') {
        return null;
    }

    const versionVideoUrl = versionEntry.videoUrl;
    if (typeof versionVideoUrl === 'string' && versionVideoUrl.length) {
        return versionVideoUrl;
    }
    return null;
}

/**
 * Timeout Function that clears the previous timeout and sets a new one.
 * @type {function(): function(*=, *=): void}
 */
const delay = (() => {
    let timer = 0;
    return (callback, ms) => {
        clearTimeout(timer);
        timer = setTimeout(callback, ms);
    };
});

/**
 * Adds a reverse function to the prototype functions of jQuery
 * @return {*|jQuery}
 */
$.fn.reverse = function () {
    return this.pushStack(this.get().reverse(), arguments);
};

// The easing function used for scrolling the page.
const scrollEase = 'linear';

/**
 * @param {Object} props
 * @param {string} props.videoUrl
 * @param {string} props.ariaLabel
 * @param {string} [props.className]
 */
const VersionHistoryVideoLink = ({videoUrl, ariaLabel, className}) => (
    <IconButton
        component={'a'}
        href={videoUrl}
        target={'_blank'}
        rel={'noreferrer'}
        className={className}
        aria-label={ariaLabel}
        size={'small'}
        color={'primary'}
    >
        <OndemandVideoOutlined fontSize={'small'}/>
    </IconButton>
);

const VersionsView = () => {
    const history = useHistory()
    const [versions, setVersions] = useState({});
    const [loading, setLoading] = useState(true);
    const isMounted = useIsMounted();
    const {t} = useTranslation('views', {keyPrefix: 'versions'});
    const {packages} = useAccessLevels();
    const showVideoLinks = packages.base.connectedAppsBizLearn;

    /**
     * Fetches the list of live tasks for the current sprint of the users selected application and if the response
     * is successful, sets the single.
     */
    const getVersions = useCallback(async () => {
        const response = await Api.getVersionHistory();
        if (!isMounted()) return;
        setVersions(response ?? {});
        setLoading(false);
    }, [isMounted])

    /**
     * Listens for the changes in the applicationToken and with each change, fetches the data from api.
     */
    useEffect(() => {
        void getVersions();
    }, [getVersions])

    /**
     * Listens for the changes in loading and versions and with each change
     * if not loading and versions have a valid length, sets up the required animations for the versions in the DOM.
     */
    useEffect(() => {
        if (loading || !Object.values(versions ?? {})?.length) return;
        setupAnimations();
    }, [loading, versions])


    /**
     * Sets up the animation and event listeners and then scrolls the window to initiate some of the event attachments.
     */
    const setupAnimations = () => {
        setupFade();
        setupClickToScroll();
        setupVersionAnimation();
        setupScrollToTop();
        enableScrollAbortion();
        $(window).scroll();
    }

    /**
     * Allows user to cancel scroll animation by manually scrolling
     */
    const enableScrollAbortion = () => {
        const $viewport = $('html, body');
        $viewport.on('scroll mousedown DOMMouseScroll mousewheel keyup', (event) => {
            if (event.which > 0 || event.type === 'mousedown' || event.type === 'mousewheel') {
                $viewport.stop();
            }
        });
    }

    /**
     * Binds the onClick handler for scrolling to the top of the window
     */
    const setupScrollToTop = () => {
        $('.trigger-scroll-to-top').click((event) => {
            event.preventDefault();
            $('html, body').animate({scrollTop: 0}, 0, scrollEase);
        });

    }

    /**
     * Listens the changes of the scroll and resize of the window and for each change:
     * For each post in the window, if the post is not in the bounds of the window then attaches a hidden class
     * otherwise removes it.
     */
    const setupVersionAnimation = () => {
        const posts = $('.post-wrapper .post');

        $(window).on('scroll resize', () => {
            const currScroll = $(window).scrollTop() > $(document).scrollTop() ? $(window).scrollTop() : $(document).scrollTop(),
                windowHeight = $(window).height(), // Needs to be here because window can resize
                overScroll = Math.ceil(windowHeight * .20),
                threshHold = (currScroll + windowHeight) - overScroll;

            posts.each(function () {
                const post = $(this);
                const postScroll = post.offset().top;

                if (postScroll > threshHold) {
                    post.addClass('hidden');
                } else {
                    post.removeClass('hidden');
                }
            });

        });
    }

    /**
     * Listens the changes of the scroll and resize of the window and for each change:
     * For each post in the window if the post in in the top half of the screen, sets an active class and then gets
     * their color and adds their appropriate color.
     */
    const setupFade = () => {
        const posts = $('.post-wrapper .post').reverse();
        const stemWrapper = $('.stem-wrapper');
        const halfScreen = $(window).height() / 2;

        $(window).on('scroll resize', () => {
            delay()(() => {
                const currScroll = $(window).scrollTop() > $(document).scrollTop() ? $(window).scrollTop() : $(document).scrollTop();
                const scrollSplit = currScroll + halfScreen;

                posts.removeClass('active').each(function () {
                    const post = $(this);
                    const postOffset = post.offset().top;

                    if (scrollSplit > postOffset) {
                        // Add active class to fade in
                        post.addClass('active')

                        // Get post color
                        const color = post.data('stem-color') ? post.data('stem-color') : null;
                        const allColors = 'color-1 color-2 color-3';

                        stemWrapper.removeClass(allColors);

                        if (color !== null) {
                            stemWrapper.addClass('color-' + color);
                        }
                        return false;
                    }
                });
            }, 20);

        });
    }

    /**
     * Sets up the onClick event for each of the posts and stem icons.
     * By clicking on each of the icons
     */
    const setupClickToScroll = () => {
        // const scrollSpeed = 100;
        const scrollEase = 'linear';

        $('.post-wrapper .post .stem-overlay .icon').click(function (e) {
            e.preventDefault();
            const icon = $(this);
            const post = icon.closest('.post');
            const postTopOffset = post.offset().top;
            const postHeight = post.height();
            const halfScreen = $(window).height() / 2;
            const scrollTo = postTopOffset - halfScreen + (postHeight / 2);

            $('html, body').animate(
                {scrollTop: scrollTo},
                0,
                // scrollSpeed,
                scrollEase
            );
        });

    }

    if (loading) {
        return <></>
    }

    return (
        <div className={'versions'}>
            <div className="stem-wrapper">
                <div className="stem"/>
                <div className="stem-background"/>
                <div className="stem-background-transparent"/>
            </div>

            <header className="section header">
                <div className="section-inner">

                    <div className="master-head">
                        <h1 className="page-title">Version History</h1>
                        <Button
                            className='my-3'
                            onClick={() => history.push(routes.landing)}
                            variant={'text'}
                            color={'primary'}
                        >
                            Back to panel
                        </Button>

                    </div>

                </div>
            </header>

            <div className="section main-content">
                <div className="section-inner">
                    <div className={'stem-padding'}>
                        <StemMask className={'stem-padding-inner'}/>
                    </div>
                    <div className="post-wrapper">
                        {
                            Object
                                .entries(versions ?? {})
                                .map(([versionName, versionEntry], index) => {
                                    const {date, notes} = versionEntry ?? {};
                                    const versionVideoUrl = getVersionVideoUrl(versionEntry);

                                    return (
                                        <article key={versionName} className="post version-icon"
                                                 data-stem-color={((index % 3) + 1).toString()}>
                                            <div className="stem-overlay">
                                                <StemIcon className={'icon'}/>
                                                <StemMask className={'stem-mask'}/>
                                                {/*<div className="stem-mask"/>*/}
                                            </div>
                                            <div className="post-content">
                                                <h2 className="post-title">
                                                <span className={'post-title-main'}>
                                                    Version {versionName}
                                                    {
                                                        showVideoLinks && versionVideoUrl &&
                                                        <VersionHistoryVideoLink
                                                            videoUrl={versionVideoUrl}
                                                            ariaLabel={t('watchVersionTutorial')}
                                                            className={'version-video-link'}
                                                        />
                                                    }
                                                </span>
                                                    {
                                                        date &&
                                                        <span className={'date'}>
                                                        ({DateUtils.serverToLocal(date).format(DateUtils.dateFormat)})
                                                         <Typography component={'span'} variant={'caption'} marginInlineStart={1}>
                                                             {DateUtils.serverToLocal(date).fromNow()}
                                                         </Typography>
                                                    </span>
                                                    }
                                                </h2>
                                                {
                                                    Object.entries(notes ?? {}).map(([key, list]) => (
                                                        <div key={key}>
                                                            <h2 className="post-section-title">
                                                                {key}
                                                            </h2>
                                                            <div className="entry-content">
                                                                <ul>
                                                                    {
                                                                        list.map((note, noteIndex) => {
                                                                            const {text, videoUrl} = normalizeVersionNote(note);

                                                                            return (
                                                                                <li
                                                                                    key={`${versionName}-${key}-${noteIndex}`}
                                                                                    className={showVideoLinks && videoUrl ? 'has-video' : undefined}
                                                                                >
                                                                                    {
                                                                                        showVideoLinks && videoUrl &&
                                                                                        <VersionHistoryVideoLink
                                                                                            videoUrl={videoUrl}
                                                                                            ariaLabel={t('watchTutorial')}
                                                                                            className={'note-video-link'}
                                                                                        />
                                                                                    }
                                                                                    <span>{text}</span>
                                                                                </li>
                                                                            );
                                                                        })
                                                                    }
                                                                </ul>

                                                            </div>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </article>
                                    );
                                })
                        }
                    </div>
                    <div className="single-stem-icon scroll-to-top trigger-scroll-to-top">
                        <ScrollToTopIcon/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VersionsView;
